import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL !== undefined ? process.env.REACT_APP_API_URL : 'http://localhost:8000';

// Axios instance with JWT token interceptor
const api = axios.create({
  baseURL: API_BASE,
});

// Add token to requests automatically
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle token expiration
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// API methods
export const auth = {
  login: (email, password) => api.post('/api/auth/login', 
    new URLSearchParams({ username: email, password }),
    { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } }
  ),
  me: () => api.get('/api/auth/me'),
  logout: () => {
    localStorage.removeItem('token');
    window.location.href = '/login';
  }
};

export const medications = {
  list: (params) => api.get('/api/medications', { params }),
  get: (id) => api.get(`/api/medications/${id}`),
  claim: (id) => api.post(`/api/medications/${id}/claim`),
  release: (id) => api.post(`/api/medications/${id}/release`),
  myClaims: () => api.get('/api/medications/my-claims'),
  update: (id, data) => api.put(`/api/medications/${id}`, data),
};

export const reviews = {
  get: (id) => api.get(`/api/reviews/${id}`),
  approveSection: (id, section, approved, notes) => 
    api.put(`/api/reviews/${id}/approve-section`, { section, approved, notes }),
  finalApprove: (id) => api.post(`/api/reviews/${id}/final-approve`),
};

// Section approvals - dedicated endpoints
export const sectionApprovals = {
  save: (medicationId, sectionName, approved, notes = '') =>
    api.post('/api/section-approvals', {
      medication_id: medicationId,
      section_name: sectionName,
      approved,
      notes
    }),
  getByMedication: (medicationId) =>
    api.get(`/api/section-approvals/${medicationId}`),
};

export const admin = {
  // Medications
  getMedications: (params) => api.get('/api/admin/medications', { params }),
  forceRelease: (id) => api.post(`/api/admin/medications/${id}/force-release`),
  deleteMedication: (id) => api.delete(`/api/admin/medications/${id}`),
  bulkDeleteMedications: (ids) => {
    if (ids.length > 500) return Promise.reject(new Error('Bulk delete limited to 500'));
    return api.delete('/api/admin/medications/bulk-delete', { data: { medication_ids: ids } });
  },
  deleteAllMedications: () => api.delete('/api/admin/medications/all'),
  bulkReleaseStuck: () => api.post('/api/admin/medications/bulk-release-stuck'),
  
  // Simplification
  simplifyMedication: (id) => api.post(`/api/admin/medications/${id}/simplify`),
  sendToReview: (id) => api.post(`/api/admin/medications/${id}/send-to-review`),
  
  // Users
  getUsers: (params) => api.get('/api/admin/users', { params }),
  createUser: (userData) => api.post('/api/admin/users', userData),
  updateUser: (id, userData) => api.put(`/api/admin/users/${id}`, userData),
  toggleUserStatus: (id, status) => api.put(`/api/admin/users/${id}/status`, { status }),
  deleteUser: (id) => api.delete(`/api/admin/users/${id}`),
  getUserAudit: (id) => api.get(`/api/admin/users/${id}/audit`),

  // Analytics
  getAnalytics: (params) => api.get('/api/admin/analytics', { params }),
  
  // Payroll Export
  exportPayroll: (data) => api.post('/api/admin/payroll/export', data, { responseType: 'blob' }),
  
  // Export
  exportData: () => api.get('/api/admin/export', { responseType: 'blob' }),
  
  // Queue Management
  getQueue: (params) => api.get('/api/admin/queue', { params }),
  addToQueue: (data) => api.post('/api/admin/queue', data),
  addFromDailyMed: (setid) => api.post(`/api/admin/medications/from-dailymed?setid=${setid}`),
  getMedicationProcessingStatus: (id) => api.get(`/api/admin/medications/${id}/status`),
  bulkImportQueue: (formData) => api.post('/api/admin/queue/bulk', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  updateQueueItem: (id, data) => api.put(`/api/admin/queue/${id}`, data),
  bumpToFront: (id) => api.put(`/api/admin/queue/${id}/bump`),
  markAsUrgent: (id) => api.put(`/api/admin/queue/${id}/urgent`),
  removeFromQueue: (id) => api.delete(`/api/admin/queue/${id}`),
  getQueueStats: () => api.get('/api/admin/queue/stats'),
  checkDuplicate: (setid) => api.get('/api/admin/check-duplicate', { params: { setid } }),
  bulkFromDailymed: (setids, auto_process = true) => {
    if (setids.length > 500) return Promise.reject(new Error('Bulk add limited to 500 SetIDs'));
    return api.post('/api/admin/medications/bulk-from-dailymed', { setids }, { params: { auto_process } });
  },
};

// DailyMed Search
export const dailymed = {
  search: (query, limit = 5000) => api.get('/api/dailymed/search', { params: { query, limit } }),
  getDrugClasses: () => api.get('/api/dailymed/drug-classes'),
  getMedicationsByClass: (drugClass, limit = 5000) => api.get(`/api/dailymed/drug-classes/${encodeURIComponent(drugClass)}/medications`, { params: { limit } }),
  getRecent: (days = 30, limit = 5000) => api.get('/api/dailymed/recent', { params: { days, limit } }),
  validateSetID: (setid) => api.get(`/api/dailymed/validate/${setid}`),
  getTopPrescribed: (year = 2025, limit = 50) => api.get('/api/dailymed/top-prescribed', { params: { year, limit } }),
};

export default api;
